/**
 * Clinical Supervisor — team-wide simulations overview.
 *
 * Lists supervised therapists with their simulation library size, group
 * default count, and per-therapist add-on grant count. Click "Manage" to
 * deep-link into the therapist's Simulations tab.
 */

import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Paper,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  CircularProgress,
  Alert,
  IconButton,
  Tooltip,
  Button,
} from '@mui/material';
import {
  School as SchoolIcon,
  ArrowBack as BackIcon,
  Refresh as RefreshIcon,
} from '@mui/icons-material';
import * as clinicalSupervisorApi from '../../services/clinicalSupervisorApi';
import api from '../../services/api';

function SupervisorSimulationsView({ token }) {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [rows, setRows] = useState([]);

  const load = async () => {
    try {
      setLoading(true);
      setError('');
      const data = await clinicalSupervisorApi.getAssignedTherapists(false, token);
      const therapists = data.therapists || [];
      // Fetch grants for every therapist in parallel. The
      // /simulation-governance/supervisor/therapists/:id/simulation-grants
      // endpoint is the same one TherapistSimulationGrants uses, so the
      // overview reflects exactly what the per-therapist tab shows.
      const grantResults = await Promise.allSettled(
        therapists.map(t =>
          api.get(`/simulation-governance/supervisor/therapists/${t._id}/simulation-grants`, token)
        )
      );
      const enriched = therapists.map((t, i) => {
        const r = grantResults[i];
        const d = r.status === 'fulfilled' ? (r.value?.data || {}) : {};
        return {
          _id: t._id,
          name: t.name,
          email: t.email,
          libSize: (d.library || []).length,
          methLibSize: (d.methodologyLibrary || []).length,
          defaultsCount: (d.defaults || []).length,
          methDefaultsCount: (d.methodologyDefaults || []).length,
          grantsCount: (d.grants || []).length,
          methGrantsCount: (d.methodologyGrants || []).length,
          visibilityOverride: d.therapistVisibilityOverride || 'inherit',
          groupVisibility: d.groupVisibility || 'supervisor_gated',
          loadError: r.status === 'rejected' ? (r.reason?.response?.data?.error || 'load failed') : null,
        };
      });
      setRows(enriched);
    } catch (err) {
      console.error('Error loading supervisor simulations overview:', err);
      setError(err.response?.data?.msg || err.message || 'Failed to load');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, [token]);

  const goManage = (therapistId) => {
    navigate(`/clinical-supervisor/therapists/${therapistId}`, {
      state: { focusTab: 'simulations' },
    });
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="60vh">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
        <IconButton onClick={() => navigate('/clinical-supervisor/dashboard')} size="small">
          <BackIcon />
        </IconButton>
        <SchoolIcon color="primary" />
        <Typography variant="h5" sx={{ flex: 1 }}>Simulations — team overview</Typography>
        <Tooltip title="Refresh">
          <IconButton onClick={load} size="small"><RefreshIcon /></IconButton>
        </Tooltip>
      </Box>

      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Each therapist receives the service group's default simulations automatically. Use "Manage" to grant
        additional scenarios or override the AI feedback visibility for an individual therapist.
      </Typography>

      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>{error}</Alert>}

      <TableContainer component={Paper}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Therapist</TableCell>
              <TableCell align="center">Library<br/>(scenarios)</TableCell>
              <TableCell align="center">Group defaults<br/>(scenarios)</TableCell>
              <TableCell align="center">Add-ons<br/>(scenarios)</TableCell>
              <TableCell align="center">Library<br/>(rubrics)</TableCell>
              <TableCell align="center">Group defaults<br/>(rubrics)</TableCell>
              <TableCell align="center">Add-ons<br/>(rubrics)</TableCell>
              <TableCell>Feedback visibility</TableCell>
              <TableCell align="right">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {rows.length === 0 && (
              <TableRow>
                <TableCell colSpan={9} align="center">
                  <Typography variant="body2" color="text.secondary" sx={{ py: 3 }}>
                    No therapists assigned to you yet.
                  </Typography>
                </TableCell>
              </TableRow>
            )}
            {rows.map(r => (
              <TableRow key={r._id} hover>
                <TableCell>
                  <Typography variant="body2" fontWeight={500}>{r.name}</Typography>
                  <Typography variant="caption" color="text.secondary">{r.email}</Typography>
                  {r.loadError && (
                    <Typography variant="caption" color="error" display="block">
                      Could not load grants: {r.loadError}
                    </Typography>
                  )}
                </TableCell>
                <TableCell align="center">{r.libSize}</TableCell>
                <TableCell align="center">
                  <Chip size="small" label={r.defaultsCount} color="success" variant="outlined" />
                </TableCell>
                <TableCell align="center">
                  <Chip size="small" label={r.grantsCount}
                    color={r.grantsCount > 0 ? 'primary' : 'default'} variant="outlined" />
                </TableCell>
                <TableCell align="center">{r.methLibSize}</TableCell>
                <TableCell align="center">
                  <Chip size="small" label={r.methDefaultsCount} color="success" variant="outlined" />
                </TableCell>
                <TableCell align="center">
                  <Chip size="small" label={r.methGrantsCount}
                    color={r.methGrantsCount > 0 ? 'primary' : 'default'} variant="outlined" />
                </TableCell>
                <TableCell>
                  {r.visibilityOverride === 'inherit' ? (
                    <Typography variant="caption" color="text.secondary">
                      Inherit ({r.groupVisibility.replace(/_/g, ' ')})
                    </Typography>
                  ) : (
                    <Chip size="small" label={r.visibilityOverride.replace(/_/g, ' ')} variant="outlined" />
                  )}
                </TableCell>
                <TableCell align="right">
                  <Button size="small" variant="outlined" onClick={() => goManage(r._id)}>
                    Manage
                  </Button>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
}

export default SupervisorSimulationsView;
